import {FeatureGate, FeatureGateTier, ProjectFeatureFlag, RestrictEntitySaving} from 'common/report/constants';
import {CurrentProject} from 'common/report/model-resources/projects';
import {savedReportLimitInfo} from 'common/report/stores/saved-reports-limit';
import {UpsellType} from 'common/report/util/tracking-helpers';

export interface UpsellInfo {
  title: string;
  description: string;
  upsellType: UpsellType;
  trackingFeatureGate: string; // Connect this to feature gates in iron/common/report/util/tracking-helpers.ts
}

export interface Metadata {
  currentCustomAlerts?: number;
}

type UpsellCheck = (currentProject: CurrentProject, metadata?: Metadata) => UpsellInfo | null;

// Keep these in alphabetical order
export enum ModalTrigger {
  ADVANCED_SHARING = `advanced_sharing`, // DO NOT USE THIS IN AN ACTUAL MODAL, but consolidate data here
  ALERTS = `alerts`,
  COHORT = `cohort`,
  EVENTS = `events`,
  FORMULA = `formula`,
  METRICS = `metrics`,
  SAVE_REPORTS = `saved_report`,
  SPARK = `spark`,
  TESTING = `testing`, // shouldn't happen, but useful for storybook
}

/**
 * This constant encapsulates the logic for determining if a modal should be shown
 * as well as the content of the modal and any metadata.
 */
const TRIGGER_TO_CHECK: Partial<Record<ModalTrigger, UpsellCheck>> = {
  [ModalTrigger.ADVANCED_SHARING]: (currentProject: CurrentProject) => {
    if (!currentProject.canSaveEntity(RestrictEntitySaving.Cohorts)) {
      return {
        title: `Want more control over your sharing permissions?`,
        description: `Upgrade to the Growth plan to decide who can view and edit your boards.`,
        upsellType: UpsellType.FREE_TO_GROWTH,
        trackingFeatureGate: `sharing`,
      };
    }
    return null;
  },
  [ModalTrigger.ALERTS]: (currentProject: CurrentProject, metadata?: Metadata) => {
    if (!currentProject.getFeatureGate(FeatureGate.AllowCustomAlerts)) {
      return {
        title: `Get Notified When Something Goes Wrong`,
        description: `Create alerts that let you know when your traffic is spiking, dropping off, or doing something notable by upgrading to Mixpanel’s Growth plan.`,
        upsellType: UpsellType.FREE_TO_GROWTH,
        trackingFeatureGate: `alerts`,
      };
    }

    const alertLimit = currentProject.getFeatureGate(FeatureGate.MaxCustomAlerts);

    if (alertLimit && metadata?.currentCustomAlerts && metadata.currentCustomAlerts >= alertLimit) {
      return {
        title: `Upgrade for More Alerts`,
        description: `Your current plan allows for up to ${alertLimit} active alerts. To create additional alerts and unlock more premium features, upgrade to the Enterprise plan.`,
        upsellType: UpsellType.GROWTH_TO_ENTERPRISE,
        trackingFeatureGate: `alerts`,
      };
    }

    return null;
  },
  [ModalTrigger.COHORT]: (currentProject: CurrentProject) => {
    if (!currentProject.canSaveEntity(RestrictEntitySaving.Cohorts)) {
      return {
        title: `Track Cohorts Across Reports`,
        description: `Segment and compare your user base with ease. Upgrade to save unlimited cohorts with Mixpanel’s Growth plan.`,
        upsellType: UpsellType.FREE_TO_GROWTH,
        trackingFeatureGate: `cohorts`,
      };
    }
    return null;
  },
  [ModalTrigger.TESTING]: (_: CurrentProject) => {
    return {
      title: `Upgrade your plan now!`,
      description: `Upgrade to access all the features below.`,
      upsellType: UpsellType.FREE_TO_GROWTH,
      trackingFeatureGate: ``,
    };
  },
  [ModalTrigger.FORMULA]: (currentProject: CurrentProject) => {
    if (!currentProject.canSaveEntity(RestrictEntitySaving.Formulas)) {
      return {
        title: `Stop Reinventing Every Report`,
        description: `Upgrade to save and reuse key analytical building blocks like formulas and metrics with Mixpanel’s Growth plan.`,
        upsellType: UpsellType.FREE_TO_GROWTH,
        trackingFeatureGate: `formulas`,
      };
    }
    return null;
  },
  [ModalTrigger.METRICS]: (currentProject: CurrentProject) => {
    if (!currentProject.canSaveEntity(RestrictEntitySaving.BehaviorMetrics)) {
      return {
        title: `Stop Reinventing Every Report`,
        description: `Upgrade to save and reuse key analytical building blocks like formulas and metrics with Mixpanel’s Growth plan.`,
        upsellType: UpsellType.FREE_TO_GROWTH,
        trackingFeatureGate: `saved_metrics`,
      };
    }
    return null;
  },
  [ModalTrigger.SAVE_REPORTS]: (currentProject: CurrentProject) => {
    const {isBelowLimit} = savedReportLimitInfo(currentProject.id);
    if (!isBelowLimit) {
      return {
        title: `Oh No! You’ve Hit Your Saved Report Limit`,
        description: `Your current plan provides up to 5 saved reports per user. To create unlimited reports and unlock premium features, upgrade to the Growth plan.`,
        upsellType: UpsellType.FREE_TO_GROWTH,
        trackingFeatureGate: `bookmark`,
      };
    }
    return null;
  },
  [ModalTrigger.SPARK]: (currentProject: CurrentProject) => {
    // We currently do not have an endpoint and it would be expensive to have to
    // re-call the endpoint everytime we want to check for the modal. Instead,
    // defer to client code to handle this.
    if (!currentProject.isFreeFeatureGateTier()) {
      return null;
    }

    return {
      title: `Upgrade for Additional Spark AI Reports`,
      description: `Your current plan provides up to 30 Spark AI reports per month. Keep the fire going! To access additional Spark reports and premium features, upgrade to the Growth plan.`,
      upsellType: UpsellType.FREE_TO_GROWTH,
      trackingFeatureGate: `bookmark`,
    };
  },
  [ModalTrigger.EVENTS]: (currentProject: CurrentProject) => {
    if (!currentProject.isFreeFeatureGateTier()) {
      return null;
    }

    if (currentProject.blocking.isBlockedEvents) {
      return {
        title: `You’ve reached your monthly events limit`,
        description: `You’re on a roll! Your current plan allows up to 1 million tracked events per month. To track more events and get the most out of Mixpanel, upgrade to the Growth plan.`,
        upsellType: UpsellType.FREE_TO_GROWTH,
        trackingFeatureGate: `bookmark`,
      };
    }

    return null;
  },
};

const getUpsellInfoForTrigger = (
  currentProject: CurrentProject,
  modalTrigger: ModalTrigger,
  metadata?: Metadata,
): UpsellInfo | null => {
  if (!currentProject) {
    return null;
  }

  const infoCallback = TRIGGER_TO_CHECK[modalTrigger];

  if (!infoCallback) {
    return null;
  }

  return infoCallback(currentProject, metadata);
};

/**
 * Check if the modal should be rendered based on the current project and modal trigger.
 */
export const getUpsellInfo = (
  currentProject: CurrentProject,
  modalTrigger: ModalTrigger,
  metadata?: Metadata,
): UpsellInfo | null => {
  if (!currentProject?.hasFeatureFlag(ProjectFeatureFlag.InProductUpsellsV2)) {
    return null;
  }

  if (currentProject.featureGateTier === FeatureGateTier.Enterprise) {
    return null;
  }

  return getUpsellInfoForTrigger(currentProject, modalTrigger, metadata);
};
